import { useState } from "react";
import { Formik, Form as FormikForm } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { TextInputComp, FormikTextAreaComp } from "..";
import InvestorModal, { InvestorModalActions } from "./InvestorModal";
import OnlinePaymentRailsChooser from "./OnlinePaymentRailsChooser";
import { formatMoney, getMemberTransactionChargesTotal } from "../../utils";
import {
  useGetInvestorPaymentFeePreviewQuery,
  useSubmitInvestorInvestmentRequestMutation,
} from "../../store/investor/investorInvestmentsApi";
import { buildInvestmentRequestPayload } from "../../utils/investorInvestmentsAdapter";
import {
  getFrontendBaseUrl,
  redirectToMemberOnlinePayment,
} from "../../utils/memberOnlinePayment";

const buildSchema = (minUnits, maxUnits) =>
  Yup.object({
    units: Yup.number()
      .required("Number of units is required")
      .min(minUnits, `Minimum ${minUnits} unit(s)`)
      .max(maxUnits, `Maximum ${maxUnits} unit(s)`),
    note: Yup.string(),
  });

function profileFullName(profile) {
  if (!profile) return "";
  return (
    profile.full_name ||
    profile.name ||
    `${profile.first_name || ""} ${profile.last_name || ""}`.trim()
  );
}

function FeeBreakdown({ principal, feePreview, isFetching }) {
  const charges = getMemberTransactionChargesTotal(feePreview);
  const totalToPay = feePreview?.member_payable_total ?? principal;
  const orgCovered =
    (feePreview?.convenience_fee_org_portion || 0) > 0 ||
    (feePreview?.organization_gateway_fee_estimate || 0) > 0;

  return (
    <div className="w-full mt-3 pt-3 border-top d-flex flex-column gap-2">
      <div className="d-flex justify-content-between align-items-center">
        <p className="text-sm text-[#525252] mb-0">Principal</p>
        <p className="text-sm fw-semibold mb-0">{formatMoney(principal, "NGN")}</p>
      </div>
      <div className="d-flex justify-content-between align-items-center">
        <p className="text-sm text-[#525252] mb-0">Transaction charges</p>
        <p className="text-sm fw-semibold mb-0">
          {isFetching ? "…" : formatMoney(charges, "NGN")}
        </p>
      </div>
      <div className="d-flex justify-content-between align-items-center border-top pt-2 mt-1">
        <p className="text-base fw-semibold text-[#1E1E1E] mb-0">Total to Pay</p>
        <p className="font-bold text-lg mb-0" style={{ color: "var(--color-primary)" }}>
          {isFetching ? "…" : formatMoney(totalToPay, "NGN")}
        </p>
      </div>
      {orgCovered && (
        <p className="text-xs text-muted mb-0">
          Some charges are covered by your cooperative.
        </p>
      )}
      <p className="text-xs text-muted mb-0">
        Payment confirms your unit reservation. Choose an online method enabled
        for your cooperative.
      </p>
    </div>
  );
}

export default function InvestmentRequestModal({
  show,
  onClose,
  opportunity,
  profile,
  activeUserId,
  defaultUnits,
}) {
  const [paymentProvider, setPaymentProvider] = useState("paystack");
  const [submitRequest, { isLoading: isSubmittingApi }] =
    useSubmitInvestorInvestmentRequestMutation();

  if (!opportunity) return null;

  const minUnits = opportunity.minUnitsPurchase || 1;
  const maxUnits = opportunity.maxUnitsPurchase || 999;
  const initialUnits = defaultUnits || minUnits;
  const investorName = profileFullName(profile);
  const investorPhone = profile?.phone || "";
  const investorEmail = profile?.email || "";

  const handleClose = () => {
    onClose?.();
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!activeUserId) {
      setSubmitting(false);
      return;
    }
    if (!investorName?.trim() || !investorPhone?.trim()) {
      toast.error("Complete your profile name and phone before investing.");
      setSubmitting(false);
      return;
    }
    if (!paymentProvider) {
      toast.error("Select a payment method.");
      setSubmitting(false);
      return;
    }

    const opportunityApiId = opportunity.apiId || opportunity.id;
    try {
      const result = await submitRequest({
        opportunityId: opportunityApiId,
        body: buildInvestmentRequestPayload(
          {
            units: values.units,
            full_name: investorName.trim(),
            phone: investorPhone.trim(),
            contact_method: investorEmail ? "Email" : "Phone call",
            note: values.note,
          },
          opportunity,
          {
            payment_provider: paymentProvider,
            frontend_base_url: getFrontendBaseUrl(),
          }
        ),
      }).unwrap();

      if (result?.paymentUrl || result?.payment_url) {
        if (result.paymentReference) {
          sessionStorage.setItem("pending_payment_reference", result.paymentReference);
        }
        if (result.reference) {
          sessionStorage.setItem("pending_investment_request_ref", result.reference);
        }
        if (result.id) {
          sessionStorage.setItem("pending_investment_request_id", String(result.id));
        }
        toast.success("Redirecting to secure payment…");
        redirectToMemberOnlinePayment({
          payment_url: result.paymentUrl || result.payment_url,
          payment_reference: result.paymentReference || result.payment_reference,
          payment_provider: result.paymentProvider || paymentProvider,
        });
        return;
      }

      toast.error("Payment could not be started. Please try again.");
    } catch (error) {
      toast.error(error?.data?.message || "Unable to start investment payment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      key={`${opportunity.id}-${show}`}
      initialValues={{
        units: initialUnits,
        note: "",
      }}
      validationSchema={buildSchema(minUnits, maxUnits)}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, isSubmitting, submitForm }) => {
        const principal = (opportunity.unitPriceNgn || 0) * (Number(values.units) || 0);
        return (
          <InvestModalFormBody
            show={show}
            onClose={handleClose}
            opportunity={opportunity}
            minUnits={minUnits}
            maxUnits={maxUnits}
            principal={principal}
            submitForm={submitForm}
            isSubmitting={isSubmitting || isSubmittingApi}
            paymentProvider={paymentProvider}
            onPaymentProviderChange={setPaymentProvider}
          />
        );
      }}
    </Formik>
  );
}

function InvestModalFormBody({
  show,
  onClose,
  opportunity,
  minUnits,
  maxUnits,
  principal,
  submitForm,
  isSubmitting,
  paymentProvider,
  onPaymentProviderChange,
}) {
  const {
    data: feePreview,
    isFetching: isFeeFetching,
  } = useGetInvestorPaymentFeePreviewQuery(
    { principal_amount: principal },
    { skip: !show || !principal || principal <= 0 }
  );

  return (
    <InvestorModal
      show={show}
      onClose={onClose}
      title={`Invest — ${opportunity.name}`}
      size="lg"
      footer={
        <InvestorModalActions
          onCancel={onClose}
          onConfirm={submitForm}
          cancelText="Cancel"
          confirmText="Pay now"
          confirmVariant="primary"
          loading={isSubmitting}
          confirmDisabled={!principal || isFeeFetching || !paymentProvider}
        />
      }
    >
      <FormikForm>
        <div className="row g-2 mb-3 small">
          <div className="col-6">
            <span className="text-muted d-block">Unit price</span>
            <strong>{formatMoney(opportunity.unitPriceNgn)}</strong>
          </div>
          <div className="col-6">
            <span className="text-muted d-block">Expected return</span>
            <strong>
              {opportunity.expectedReturnPct}% / {opportunity.durationMonths}mo
            </strong>
          </div>
        </div>
        <TextInputComp
          labelText="Number of units"
          name="units"
          type="number"
          desc={`Min ${minUnits}, max ${maxUnits}`}
        />
        <FeeBreakdown principal={principal} feePreview={feePreview} isFetching={isFeeFetching} />
        <div className="mt-3">
          <OnlinePaymentRailsChooser
            paymentProvider={paymentProvider}
            onPaymentProviderChange={onPaymentProviderChange}
            compact
          />
        </div>
        <p className="text-muted small mt-3 mb-3" style={{ lineHeight: 1.55 }}>
          Contact details will be taken from your profile. Update them in Profile if anything
          needs to change.
        </p>
        <FormikTextAreaComp LabelText="Note (optional)" fieldName="note" rows={3} />
      </FormikForm>
    </InvestorModal>
  );
}
